"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { cn } from "@/shared/lib/utils"

const LINE_CLAMP: Record<2 | 3 | 4, string> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
}

interface ExpandableClampedTextProps {
  text: string
  lines?: 2 | 3 | 4
  className?: string
  toggleClassName?: string
  /** Where to align the View more / Show less control */
  toggleAlign?: "start" | "center"
  moreLabel?: string
  lessLabel?: string
}

/** Multi-line clamp with View more / Show less when content overflows. */
export function ExpandableClampedText({
  text,
  lines = 2,
  className,
  toggleClassName,
  toggleAlign = "start",
  moreLabel = "View more",
  lessLabel = "Show less",
}: ExpandableClampedTextProps) {
  const ref = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  useEffect(() => {
    setExpanded(false)
  }, [text])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (expanded) return

    const check = () => {
      setCanExpand(el.scrollHeight > el.clientHeight + 1)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, expanded, lines])

  return (
    <div className="flex flex-col gap-1">
      <p
        ref={ref}
        className={cn(className, !expanded && LINE_CLAMP[lines])}
      >
        {text}
      </p>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className={cn(
            "text-xs font-medium text-primary hover:underline",
            toggleAlign === "center" ? "self-center" : "self-start",
            toggleClassName
          )}
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  )
}
